import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
	Box,
	Button,
	Flex,
	HStack,
	Input,
	Link,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Select,
	Spacer,
} from '@chakra-ui/react';

import { defaultDetails, defaultLink, options } from './data';
import {
	formatTZ,
	formatSQLTZ,
	getDateTabs,
	getHour,
	getWeekTimezone,
	getTimezoneByWeekOffset,
	getMondayByWeekOffset,
	getOffset,
	getSlotUnix,
	getWeek,
	range,
	timeGrid,
} from '../app-data/date';

const getColor = ({ status }) => {
	if (status === 'booked') return 'teal';
	if (status === 'probono') return 'yellow';

	return 'gray';
};

export const Consultations = ({ admin, wakeCons, setWakeCons, setUserID }) => {
	const [loading, setLoading] = useState(true);
	const [cons, setCons] = useState([]);
	const [customers, setCustomers] = useState([]);
	const [weekOffset, setWeekOffset] = useState(0);
	const [fields, setFields] = useState({ tz: getWeekTimezone() });
	const [monday, setMonday] = useState(getMondayByWeekOffset(0));
	const [isOpen, setIsOpen] = useState(false);
	const [consID, setConsID] = useState(0);
	const [start, setStart] = useState('');
	const [link, setLink] = useState('');
	const [details, setDetails] = useState('');
	const [memo, setMemo] = useState('');
	const [customerID, setCustomerID] = useState(0);

	const onClose = () => setIsOpen(false);

	const handleCons = newStatus => {
		axios({
			method: 'put',
			url: `/adminCons`,
			data: {
				id: consID,
				memo,
				customerID,
				start,
				link,
				details,
				status: newStatus,
			},
			...options(),
		})
			.then(() => {
				setWakeCons(new Date());
				onClose();
			})
			.catch(console.log);
	};
	const chooseCustomer = e => setCustomerID(e.target.value);

	const onOpen = cons => {
		setConsID(cons.id);
		setStart(cons.starts_at);
		setLink(cons.link || defaultLink);
		setDetails(cons.details || defaultDetails);
		setMemo(cons.memo);
		setCustomerID(cons.customer_id);
		setIsOpen(true);
	};
	const setWeek = increment => {
		const newWeekOffset = weekOffset + increment;
		const weekTimezone = getTimezoneByWeekOffset(newWeekOffset);
		const newMonday = getMondayByWeekOffset(newWeekOffset);
		setWeekOffset(newWeekOffset);
		setFields({ tz: weekTimezone });
		setMonday(newMonday);
	};

	const prev = () => setWeek(-1);
	const next = () => setWeek(1);
	const [weekTitle, week] = getWeek(monday);

	const consultant = admin < 3 ? 1 : admin;
	useEffect(() => {
		let active = true;
		async function load() {
			const res = await axios.get(`/cons/${consultant}/${monday}`, options());
			if (!active) {
				return;
			}
			setCons(res.data.cons);
			setCustomers(res.data.customers);
			setLoading(false);
		}
		load();

		return () => {
			active = false;
		};
	}, [admin, wakeCons, consultant, monday]);

	const dateTabs = cons.reduce(getDateTabs(fields.tz), {});

	const addProposed = (monday, dayIndex, halfAnHour) => {
		const time = getSlotUnix(monday, dayIndex, halfAnHour);
		axios({
			method: 'post',
			url: `/adminCons`,
			data: { admin: consultant, newSeries: [formatSQLTZ(time, fields.tz)] },
			...options(),
		})
			.then(() => {
				setWakeCons(new Date());
			})
			.catch(console.log);
	};

	if (loading) return <p>Loading...</p>;

	return (
		<Box>
			<Flex pb={6}>
				<Button colorScheme="blue" onClick={prev}>
					prev
				</Button>
				<Spacer />
				<Box>{weekTitle}</Box>
				<Spacer />
				<Button colorScheme="blue" onClick={next}>
					next
				</Button>
			</Flex>
			<HStack
				spacing={0}
				align="top"
				borderTop="1px"
				borderLeft="1px"
				borderColor="gray.300"
			>
				{week.map((day, dayIndex) => (
					<Box
						w={`${100 / 7}%`}
						borderRight="1px"
						borderColor="gray.300"
						pos="relative"
					>
						<Box
							p={3}
							bg="gray.200"
							borderBottom="1px"
							borderColor="gray.300"
							textAlign="center"
						>
							{day.format('dd DD.MM')}
						</Box>
						{range(32).map(halfAnHour => (
							<Box
								key={`${day} ${halfAnHour}`}
								h="36px"
								bg="gray.100"
								borderBottom="1px"
								borderColor="gray.300"
								color="gray.500"
								fontSize="sm"
								onDoubleClick={() => addProposed(monday, dayIndex, halfAnHour)}
							>
								{halfAnHour % 2 === 0 &&
									getHour(
										monday + 16 * timeGrid + halfAnHour * timeGrid,
										fields.tz
									)}
							</Box>
						))}
						{(dateTabs[day.format('YYYY-MM-DD')] || []).map(cons => (
							<Box
								key={cons.id}
								pos="absolute"
								left="0"
								top={`${49 + getOffset(cons.starts_at, fields.tz) * 36}px`}
								w="100%"
								h="71px"
								bg={`${getColor(cons)}.300`}
								border="1px"
								borderColor={`${getColor(cons)}.700`}
								borderRadius="md"
								color={`${getColor(cons)}.700`}
								fontSize="sm"
								px={2}
								onDoubleClick={() => onOpen(cons)}
							>
								<Box>
									<Link
										color={`${getColor(cons)}.700`}
										href={cons.link || defaultLink}
										_hover={{
											textDecoration: 'none',
										}}
									>{`${formatTZ(cons.starts_at, fields.tz)}`}</Link>
								</Box>
								<Box>
									{cons.customer_id === 0 ? (
										'———'
									) : (
										<Box
											fontWeight="700"
											cursor="pointer"
											onClick={async () => {
												await setUserID(-1);
												await setUserID(cons.customer_id);
											}}
										>
											{`${cons.firstName} ${cons.lastName}`}
										</Box>
									)}
								</Box>
								<Box>{cons.memo || ''}</Box>
							</Box>
						))}
					</Box>
				))}
			</HStack>
			<Modal isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Edit consultation</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Box my={3}>
							<Input
								width="100%"
								value={start}
								onChange={e => setStart(e.target.value)}
							/>
						</Box>
						<Box my={3}>
							<Select
								placeholder="Choose a customer"
								onChange={chooseCustomer}
								w="100%"
							>
								{customers.map(({ id, lastName, firstName }) => (
									<option value={id} selected={customerID === id}>
										{`${lastName} ${firstName}`}
									</option>
								))}
							</Select>
						</Box>
						<Box my={3}>
							<Input
								width="100%"
								value={link}
								onChange={e => setLink(e.target.value)}
								placeholder="Enter meeting or WhatsApp (Messenger) room link"
							/>
						</Box>
						<Box my={3}>
							<Input
								width="100%"
								value={details}
								onChange={e => setDetails(e.target.value)}
								placeholder="Enter meeting details"
							/>
						</Box>
						<Box my={3}>
							<Input
								width="100%"
								value={memo}
								onChange={e => setMemo(e.target.value)}
								placeholder="Enter consultation memo"
							/>
						</Box>
					</ModalBody>

					<ModalFooter>
						<Button variant="ghost" mr={3} onClick={() => handleCons('remind')}>
							Remind
						</Button>
						<Button
							variant="ghost"
							mr={3}
							onClick={() => handleCons('probono')}
						>
							Update
						</Button>
						<Button colorScheme="red" onClick={() => handleCons('canceled')}>
							Cancel
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Box>
	);
};
